import type { VoyageIncome_VoyageCharterFragment } from '../../../graphql';
import { CommissionType } from '../../../graphql';
import { exhaustiveCheck } from '../../../lib/util';
import { convertToDefaultCurrency } from '../../../state/zustand/currencyRates';
import type { Income } from './income';
import type { LoadedAmount } from '../loadedAmounts';

export function getVoyageCharterCommissionIncomes(
  voyageCharter: VoyageIncome_VoyageCharterFragment,
  /** Freight, extra freight and baf incomes for this voyage charter. (Demurrage not implemented yet) */
  incomes: Income[],
  loadedAmount: LoadedAmount[],
) {
  const commissionIncomes: Income[] = [];
  const commissions = voyageCharter.commissionsByVoyageCharterId.nodes;

  let totalLoadedAmount = 0;
  for (const la of loadedAmount) {
    totalLoadedAmount += la.cargoAmount;
  }

  commissions.forEach((commission) => {
    if (!commission) return;

    const bafApplied = commission?.bafApplied ?? false;
    const extraFreightApplied = commission?.extraFreightApplied ?? false;

    const includedIncomes = incomes.filter((income) => {
      return (
        income.type === 'ORDER_FREIGHT' ||
        income.type === 'VOYAGE_CHARTER_LUMPSUM' ||
        (income.type === 'BAF' && bafApplied) ||
        (income.type === 'EXTRA_FREIGHT' && extraFreightApplied)
      );
    });

    let totalIncludedIncome = 0;
    for (const { value } of includedIncomes) {
      if (value !== null) {
        totalIncludedIncome += value;
      }
    }

    const commissionIncomeAmount =
      commission.type === CommissionType.LUMPSUM
        ? convertToDefaultCurrency(
            +commission.value,
            voyageCharter.currency ?? undefined,
          )
        : commission.type === CommissionType.PERCENTAGE
          ? (totalIncludedIncome / 100) * +commission.value
          : commission.type === CommissionType.RATE
            ? totalLoadedAmount * +commission.value
            : exhaustiveCheck(commission.type);

    const orgName = commission.organizationByOrganizationId?.name ?? null;

    const commissionValue =
      typeof commissionIncomeAmount === 'number'
        ? commissionIncomeAmount
          ? -commissionIncomeAmount
          : 0
        : null;

    commissionIncomes.push({
      type: 'COMMISSION',
      name: orgName ? `Commission (${orgName})` : 'Commission',
      value: commissionValue,
      orderId: null,
      voyageCharterId: voyageCharter.id,

      params: [
        {
          name: 'Type',
          value: commission.type,
        },
        {
          name: 'Value',
          value: +commission.value,
        },
        {
          name: 'Apply on BAF',
          value: bafApplied ? 'Yes' : 'No',
        },
        {
          name: 'Apply on extra freight',
          value: extraFreightApplied ? 'Yes' : 'No',
        },
        ...(commission.type === CommissionType.LUMPSUM
          ? [{ name: 'Currency', value: voyageCharter.currency ?? null }]
          : []),
      ],
    });
  });

  return commissionIncomes;
}
